import React, { useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import OnboardingWelcomeScreen from "./Components/Stages/OnboardingWelcomeScreen";
import OnboardingLanguageScreen from "./Components/Stages/OnboardingLanguageScreen";
import OnboardingPersonalDetailsScreen from "./Components/Stages/OnboardingPersonalDetailsScreen";
import OnboardingPhysicalAttributesScreen from "./Components/Stages/OnboardingPhysicalAttributesScreen";
import OnboardingProfileAvatarScreen from "./Components/Stages/OnboardingAvatarScreen";
import OnboardingInterestsScreen from "./Components/Stages/OnboardingInterestsScreen";
import OnboardingPublicAlbumPicsScreen from "./Components/Stages/OnboardingPublicAlbumPicsScreen";

import { UserOnboardingStage } from "@/utils/onboarding";
import { useSession } from "@/context/auth/useSession";
import type { AppUserSession } from "@/schemas/auth/session";
import { themes } from "@/Components/Styling/themeConfigs";

type Props = {
  session: AppUserSession;
};

const currentTheme = themes.morning;

function OnboardingScreen({ session }: Props): React.JSX.Element {
  const { refreshUserIdentity } = useSession();
  const onboardingStage = session.identity.onboardingStage;
  const insets = useSafeAreaInsets();

  useEffect(() => {
    refreshUserIdentity();
  }, []);

  return (
    <View
      style={[
        styles.container,
        { paddingTop: insets.top, paddingBottom: insets.bottom },
      ]}
    >
      <LinearGradient
        colors={currentTheme.bg}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />
      {renderCurrentScreen()}
    </View>
  );

  function renderCurrentScreen(): React.JSX.Element | null {
    switch (onboardingStage) {
      case UserOnboardingStage.WELCOME_SCREEN:
        return <OnboardingWelcomeScreen session={session} />;
      case UserOnboardingStage.PROFILE_LANGUAGE:
        return <OnboardingLanguageScreen session={session} />;
      case UserOnboardingStage.PROFILE_INTEREST:
        return <OnboardingInterestsScreen session={session} />;
      case UserOnboardingStage.PROFILE_PERSONAL_DETAILS:
        return <OnboardingPersonalDetailsScreen session={session} />;
      case UserOnboardingStage.PROFILE_PHYSICAL_ATTRIBUTES:
        return <OnboardingPhysicalAttributesScreen session={session} />;
      case UserOnboardingStage.PROFILE_AVATAR:
        return <OnboardingProfileAvatarScreen session={session} />;
      case UserOnboardingStage.PUBLIC_ALBUM_PICS:
        return <OnboardingPublicAlbumPicsScreen session={session} />;
      default:
        return null;
    }
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default OnboardingScreen;
